<template>
  <div class="billing-identity-form">
    <p v-if="intro" class="billing-identity-form__intro">{{ intro }}</p>

    <!-- The party kind is written once and cannot be changed afterwards: it decides whose name
         a document is issued in and feeds the reverse-charge rule. So it is a choice while
         there is no customer, and a read-only fact after. -->
    <v-alert
      v-if="hasCustomer"
      type="info"
      variant="tonal"
      density="comfortable"
      class="mb-6"
    >
      {{ t('settings.billing.identity.kindFrozen', { kind: t(`settings.billing.identity.kinds.${form.kind}`) }) }}
    </v-alert>

    <v-form ref="formRef" @submit.prevent="save">
      <v-row density="compact">
        <v-col v-if="!hasCustomer" cols="12">
          <v-radio-group
            v-model="form.kind"
            :label="t('settings.billing.identity.kind')"
            inline
            hide-details
            class="billing-identity-form__kind"
          >
            <v-radio
              v-for="option in kindOptions"
              :key="option.value"
              :label="option.title"
              :value="option.value"
            />
          </v-radio-group>
        </v-col>

        <!-- A company is invoiced in its registered name and the person is its contact; an
             individual is invoiced in their own name, and has neither a company nor a VAT number. -->
        <v-col v-if="isCompany" cols="12" md="6">
          <v-text-field
            v-model="form.company_name"
            :label="t('settings.billing.identity.companyName')"
            :rules="[required]"
            variant="solo-filled"
            density="comfortable"
            flat
            hide-details="auto"
          />
        </v-col>

        <v-col cols="12" :md="isCompany ? 6 : 12">
          <v-text-field
            v-model="form.name"
            :label="t(isCompany ? 'settings.billing.identity.contactName' : 'settings.billing.identity.fullName')"
            :rules="[required]"
            variant="solo-filled"
            density="comfortable"
            flat
            hide-details="auto"
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.email"
            :label="t('settings.billing.identity.email')"
            type="email"
            variant="solo-filled"
            density="comfortable"
            flat
            hide-details="auto"
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-select
            v-model="form.country"
            :items="countryOptions"
            :label="t('settings.billing.identity.country')"
            variant="solo-filled"
            density="comfortable"
            flat
            hide-details="auto"
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.line1"
            :label="t('settings.billing.identity.line1')"
            variant="solo-filled"
            density="comfortable"
            flat
            :rules="[required]"
            hide-details="auto"
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.line2"
            :label="t('settings.billing.identity.line2')"
            variant="solo-filled"
            density="comfortable"
            flat
            hide-details="auto"
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-text-field
            v-model="form.postal_code"
            :label="t('settings.billing.identity.postalCode')"
            variant="solo-filled"
            density="comfortable"
            flat
            :rules="[required]"
            hide-details="auto"
          />
        </v-col>

        <v-col cols="12" md="8">
          <v-text-field
            v-model="form.city"
            :label="t('settings.billing.identity.city')"
            variant="solo-filled"
            density="comfortable"
            flat
            :rules="[required]"
            hide-details="auto"
          />
        </v-col>

        <v-col v-if="isCompany" cols="12">
          <v-text-field
            v-model="form.vat_number"
            :label="t('settings.billing.identity.vatNumber')"
            :error-messages="vatRefused ? t('settings.billing.identity.vat.invalid') : []"
            @update:model-value="vatRefused = false"
            variant="solo-filled"
            density="comfortable"
            flat
            hide-details="auto"
          />
        </v-col>
      </v-row>

      <!-- What VIES answered, and it is evidence rather than a field: neither is writable, and
           `null` is a member state that was unavailable, which is not a failure. -->
      <div v-if="isCompany && profile?.vat_number && !vatRefused" class="billing-identity-form__vat">
        <v-icon :icon="vatIcon" size="18" :color="vatColor" />
        <span>{{ t(`settings.billing.identity.vat.${vatState}`) }}</span>
        <span v-if="profile.vat_checked_at" class="billing-identity-form__vat-date">
          {{ formatMoment(profile.vat_checked_at) }}
        </span>
      </div>

      <v-alert
        v-if="saveError"
        type="error"
        variant="tonal"
        density="comfortable"
        class="mt-4"
        closable
        @click:close="saveError = null"
      >
        {{ saveError }}
      </v-alert>

      <v-alert v-if="saved && showSavedNotice" type="success" variant="tonal" density="comfortable" class="mt-4">
        {{ t('settings.billing.identity.saved') }}
      </v-alert>

      <div class="billing-identity-form__actions" :class="{ 'billing-identity-form__actions--split': showBack }">
        <v-btn v-if="showBack" variant="text" :disabled="mutation.isPending.value" @click="emit('back')">
          {{ t('billing.identity.back') }}
        </v-btn>
        <v-btn color="primary" variant="flat" type="submit" :loading="mutation.isPending.value">
          {{ submitLabel || t('core.common.save') }}
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
/**
 * Who this organization is invoiced as — the form, shared by the settings screen and the top-up.
 *
 * **One form, two places.** The settings screen (`BillingIdentityView`) edits it at leisure; the
 * top-up asks it once, between the amount and the payment, while the profile is incomplete
 * (`isBillingProfileComplete`), and goes on to the payment when it is saved. Both read the same
 * cached profile (`useBillingProfile`) and the same save, so the copy is the settings screen's
 * (`settings.billing.identity.*`) wherever it is shown.
 *
 * **A `PUT` and not a `PATCH`.** A first save is what creates the customer, so it carries the
 * identity whole and an emptied field clears rather than being left alone. What the SIREN lookup
 * used to prefill is gone with it: three of the six fields that service answered — SIREN, legal
 * form, registration status — land in no column here and in none at the invoicing service either,
 * so the route was deliberately not ported and the number is typed.
 *
 * The caller owns the loading and the read error: this renders once there is a profile to seed
 * from, or none at all.
 */
import { billingFailure } from '@/apps/billing/failure'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiAlertCircleOutline, mdiCheckCircleOutline, mdiHelpCircleOutline } from '@mdi/js'
import { billingCountries } from '@/apps/billing/utils/countries'
import type {
  BillingAddress,
  BillingPartyKind,
  BillingProfileView,
} from '@/api/billing/profile'
import { useBillingProfile, useUpdateBillingProfile } from '@/apps/billing/queries'

withDefaults(
  defineProps<{
    /** A sentence above the form; omitted inside a screen that already explains itself. */
    intro?: string
    /** The button's label; *save* when omitted. */
    submitLabel?: string
    /** A *back* button beside it, for a flow with a step before this one. */
    showBack?: boolean
    /** Say *saved* under the form — for a screen that stays where it is after a save. */
    showSavedNotice?: boolean
  }>(),
  { intro: '', submitLabel: '', showBack: false, showSavedNotice: true },
)

const emit = defineEmits<{ saved: [BillingProfileView]; back: [] }>()

const { t, locale } = useI18n()

const query = useBillingProfile()
const profile = computed(() => query.data.value ?? null)
/** Every field is `null` until a first save or a first top-up creates the customer. */
const hasCustomer = computed(() => profile.value?.customer_id != null)

const mutation = useUpdateBillingProfile()
const saveError = ref<string | null>(null)
const saved = ref(false)
/** The form, so a save is refused on the fields' own rules before anything is sent. */
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null)

interface FormState {
  kind: BillingPartyKind
  name: string
  company_name: string
  email: string
  line1: string
  line2: string
  postal_code: string
  city: string
  country: string
  vat_number: string
}

const form = ref<FormState>({
  kind: 'company',
  name: '',
  company_name: '',
  email: '',
  line1: '',
  line2: '',
  postal_code: '',
  city: '',
  country: 'FR',
  vat_number: '',
})

watch(
  profile,
  (value) => {
    if (!value) return
    const address: BillingAddress = value.address ?? {}
    form.value = {
      kind: (value.kind as BillingPartyKind | null) ?? 'company',
      name: value.name ?? '',
      company_name: value.company_name ?? '',
      email: value.email ?? '',
      line1: address.line1 ?? '',
      line2: address.line2 ?? '',
      postal_code: address.postal_code ?? '',
      city: address.city ?? '',
      country: value.country ?? 'FR',
      vat_number: value.vat_number ?? '',
    }
  },
  { immediate: true },
)

const isCompany = computed(() => form.value.kind === 'company')

/**
 * VIES refused the number just saved. The invoicing service keeps it anyway — a refused number only
 * means no reverse charge — so the save succeeded; but a number the person typed and that nobody
 * recognises is a mistake to correct, so it is said on the field and the flow does not go on.
 */
const vatRefused = ref(false)

const kindOptions = computed(() => [
  { value: 'company', title: t('settings.billing.identity.kindOptions.company') },
  { value: 'individual', title: t('settings.billing.identity.kindOptions.individual') },
])

const countryOptions = computed(() =>
  billingCountries(locale.value).map((country) => ({ value: country.code, title: country.name })),
)

const vatState = computed(() => {
  if (profile.value?.vat_valid === true) return 'valid'
  if (profile.value?.vat_valid === false) return 'invalid'
  return 'unknown'
})
const vatIcon = computed(() => {
  if (vatState.value === 'valid') return mdiCheckCircleOutline
  if (vatState.value === 'invalid') return mdiAlertCircleOutline
  return mdiHelpCircleOutline
})
const vatColor = computed(() => {
  if (vatState.value === 'valid') return 'success'
  if (vatState.value === 'invalid') return 'error'
  return 'grey'
})

function required(value: string): boolean | string {
  return !!value || t('core.validation.required')
}

function errorOf(error: unknown): string {
  return billingFailure(error, t('core.common.errorLoading'))
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

async function save(): Promise<void> {
  saveError.value = null
  saved.value = false
  vatRefused.value = false
  // A required field left empty is the form's to say, next to the field: sent anyway, it comes
  // back from the invoicing service as a refusal that names nothing.
  const checked = await formRef.value?.validate()
  if (checked && !checked.valid) return
  try {
    const answer = await mutation.mutateAsync({
      kind: form.value.kind,
      name: form.value.name.trim(),
      // An individual has no company and no VAT number: whatever an earlier choice left in those
      // fields is not sent.
      company_name: isCompany.value ? blankToNull(form.value.company_name) : null,
      email: blankToNull(form.value.email),
      // The four parts the invoicing service holds and no other: `postal_code` is what tells
      // Cayenne from Paris in its tax resolution. A part left empty is sent as `null` and never
      // reaches that service, which refuses a blank one.
      address: {
        line1: blankToNull(form.value.line1),
        line2: blankToNull(form.value.line2),
        postal_code: blankToNull(form.value.postal_code),
        city: blankToNull(form.value.city),
      },
      country: form.value.country,
      vat_number: isCompany.value ? blankToNull(form.value.vat_number) : null,
    })
    if (answer.vat_number && answer.vat_valid === false) {
      vatRefused.value = true
      return
    }
    saved.value = true
    emit('saved', answer)
  } catch (error) {
    saveError.value = errorOf(error)
  }
}

function formatMoment(value: string): string {
  return new Date(value).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.billing-identity-form {
  &__intro {
    margin: 0 0 $space-lg;
    font-size: $text-md;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__kind {
    margin-bottom: $space-sm;
  }

  &__vat {
    display: flex;
    align-items: center;
    gap: $space-sm;
    margin-top: $space-lg;
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__vat-date {
    font-size: $text-xs;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: $space-sm;
    margin-top: $space-xl;

    &--split {
      justify-content: space-between;
    }
  }
}
</style>
